import { useModalProvider } from "@/components/modal-popup/modal-popup";
import { UserInfoEntity } from "@/domain/entity/user-info-entity";
import { DownloadUserManualUsecase } from "@/domain/usecase/common/download-user-guide/download-user-manual-usecase";
import { FetchHeaderUserInfoUsecase } from "@/domain/usecase/common/header/fetch-header-user-info-usecase";
import { LogoutUsecase } from "@/domain/usecase/common/login/logout-usecase";
import {
  clearPasswordChangeCookies,
  clearUserRoleCookie,
  getPasswordChangeType,
} from "@/utils/cookie-utils";
import { getHomeHref, getPasswordChangeHref } from "@/utils/enum/user-role";
import { useRoleAccess } from "@/utils/hooks/use-role-access";
import { useTranslation } from "@/utils/i18n/client";
import { SATError } from "@sadtx/sat-error";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

interface HeaderViewModelDependencies {
  fetchHeaderUserInfoUsecase: FetchHeaderUserInfoUsecase;
  logoutUsecase: LogoutUsecase;
  downloadUserManualUsecase: DownloadUserManualUsecase;
}

export function useHeaderViewModel(deps: HeaderViewModelDependencies) {
  //MARK:- Properties
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [isLoading, setIsLoading] = useState(false);
  const { showModal } = useModalProvider();
  const { t } = useTranslation();

  //MARK:- State
  const [userInfo, setUserInfo] = useState<UserInfoEntity | null>(null);

  //MARK:- Role Access Control
  const { showAccessDenied, requiredRole } = useRoleAccess({ userRole: userInfo?.role || null });

  //MARK: DI
  useEffect(() => {
    setIsLoading(true);
    console.log("[HeaderViewModel] :: Constructor called with dependencies", deps);
    const fetchUserInfo = async (): Promise<void> => {
      const result = await deps.fetchHeaderUserInfoUsecase.execute();
      if (result.isFailure) {
        console.error("[HeaderViewModel] :: fetchUserInfo :: failure", result.error);
        handleError(result.error);
        setIsLoading(false);
        return;
      }
      setUserInfo(result.value);
      setIsLoading(false);
    };
    fetchUserInfo();
  }, []);

  // const handleSSEMessage = async (data: PreRegistrationPatientInfoEntity) => {
  //   console.log("[HeaderViewModel] :: handleSSEMessage :: ", data);
  //   addPreRegistrationToast({
  //     preRegistrationInfo: data,
  //     handleEnrollAction: () => {
  //       console.log(t("header.console.registerAction"));
  //     },
  //   });
  // };

  // const checkUserToken = async () => {
  //   console.log("[HeaderViewModel] :: checkUserToken :: start");
  //   setIsLoading(true);
  //   const result = await dependencies.userRequester.userTokenCheck();
  //   if (result.isFailure) {
  //     console.log("[HeaderViewModel] :: checkUserToken :: failure");
  //     setIsLoading(false);
  //     const error = result.error;
  //     showModal({
  //       error: error,
  //       title: error.name,
  //       description: [error.message],
  //       okButton: {
  //         title: t("common.ok"),
  //         onClick: () => {
  //           navigate("/", { replace: true });
  //         },
  //       },
  //     });
  //     return;
  //   }
  //   setIsLoading(false);
  // };

  //MARK:- BT Action
  const handleLogoClick = () => {
    const userRole = userInfo?.role;
    if (!userRole) {
      return;
    }
    // 패스워드 변경이 필요한지 확인
    const passwordChangeRequired = getPasswordChangeType();

    // 패스워드 변경이 필요한 경우 패스워드 변경 페이지로 리다이렉트
    if (passwordChangeRequired) {
      const passwordChangeRoute = getPasswordChangeHref(userRole);
      navigate(passwordChangeRoute, { replace: true });
      return;
    }

    // 역할에 따른 홈 경로로 라우팅
    const homeRoute = getHomeHref(userInfo?.role);
    navigate(homeRoute);
  };

  const handleLogout = async () => {
    setIsLoading(true);
    await deps.logoutUsecase.execute();
    console.log(`[HeaderViewModel] ${t("header.console.logoutAction")}`);
    // 쿠키 정리 (시큐어 스토리지 암복호화 키 삭제는 LogoutUsecase에서 처리)
    clearUserRoleCookie();
    clearPasswordChangeCookies();
    navigate("/", { replace: true });
  };

  const handlePasswordChange = () => {
    // 사용자 역할에 따라 적절한 경로로 라우팅
    const passwordChangeRoute = getPasswordChangeHref(userInfo?.role ?? pathname);
    console.log(
      "[HeaderViewModel] :: handlePasswordChange :: passwordChangeRoute",
      passwordChangeRoute
    );
    // 헤더에서 패스워드 변경 화면으로 이동 할때만 breadcrumb 표시
    navigate(passwordChangeRoute, { state: { showBreadcrumbFromHeader: true } });
  };

  const handleUserManualDownload = async () => {
    setIsLoading(true);
    const result = deps.downloadUserManualUsecase.execute();
    if (result.isFailure) {
      console.error("[HeaderViewModel] :: handleUserManualDownload :: failure", result.error);
      setIsLoading(false);
      showModal({
        error: null,
        title: "사용자 메뉴얼 다운로드 실패",
        description: ["팝업 차단 등으로 사용자 메뉴얼을 열 수 없습니다. 잠시후 다시 시도해주세요."],
        okButton: {
          title: t("common.ok"),
          onClick: () => {},
        },
      });
      return;
    }

    setIsLoading(false);
  };

  const handleError = (error: SATError) => {
    console.error("[HeaderViewModel] :: handleError :: ", error);
    showModal({
      error: error,
      title: error.name,
      description: [error.message],
      okButton: {
        title: t("common.ok"),
        onClick: () => {},
      },
    });
  };

  //MARK:- Return
  return {
    userInfo,
    isLoading,
    // checkUserToken,
    handleLogoClick,
    handleLogout,
    handlePasswordChange,
    handleUserManualDownload,
    // Role Access Control
    showAccessDenied,
    requiredRole,
  };
}
